import { createContext, FC, useEffect } from 'react'
import { FormProvider, useFieldArray, useForm, UseFormReset } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '@microservices/wiskey-react-components'
import { Add } from '@mui/icons-material'
import { Grid, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { getDefaultVariable } from '@components/Variables/helpers'
import { useVariables } from '@components/Variables/hooks/useVariables'
import { VariablesForm, VariableValue } from '@components/Variables/types'
import { Variable } from '@components/Variables/Variable'

import { ENTITY_TYPES_REQUEST } from '@constants'

type VariablesProps = {
  isEditEntity: boolean
  entityType: ENTITY_TYPES_REQUEST
  entityId?: number | string
}

type PageVariablesType = {
  isCreateOrEdit: boolean
  onCreateOrEdit: (value: boolean) => void
  currentVariables?: VariableValue[]
  isCreateOrEditVariable?: boolean
  entityType: ENTITY_TYPES_REQUEST
  entityId?: number | string
  reset: UseFormReset<VariablesForm>
}

export const PageVariablesContext = createContext<PageVariablesType>({} as PageVariablesType)

export const Variables: FC<VariablesProps> = ({
  isEditEntity,
  entityType,
  entityId,
}: VariablesProps) => {
  const { t } = useTranslation()

  const { currentVariables, isCreateOrEdit, handleCreateOrEdit } = useVariables({
    isEditEntity,
    entityType,
    entityId,
  })

  const methods = useForm<VariablesForm>({
    defaultValues: {
      variables: [],
    },
    mode: 'all',
    reValidateMode: ['onChange', 'onSubmit', 'onBlur'],
  })

  const { reset } = methods

  const {
    fields: variables,
    insert,
    remove,
  } = useFieldArray({
    control: methods.control,
    name: 'variables',
  })

  useEffect(() => {
    if (currentVariables && currentVariables.length > 0) {
      reset({
        variables: currentVariables.map(variable => {
          return {
            ...variable,
            variableType: { id: variable.variableType, label: variable.variableType },
            valueType: { id: variable.valueType, label: variable.valueType },
            isEdit: false,
          }
        }),
      })
    }
  }, [currentVariables, methods, entityId])

  const handleAddVariable = () => {
    const { variables: defaultVars } = getDefaultVariable()
    const emptyValue = {
      ...defaultVars[0],
      isEdit: true,
    }

    insert(0, emptyValue)
  }

  const isCreateOrEditVariable = variables.some(variable => variable.isEdit)

  const handleRemoveVariable = (index: number) => {
    remove(index)
  }

  const sortedVariables = [...variables].sort((a, b) => {
    if (a.isEdit && !b.isEdit) return -1 // isEdit = true идет первым
    if (!a.isEdit && b.isEdit) return 1

    return 0
  })

  const valuesVariables = methods.watch('variables') || []
  // актуальные обновленные данные
  const enrichedVariables = variables.map((field, index) => ({
    ...field,
    ...(valuesVariables[index] || {}),
  }))

  const contextValue = {
    entityType,
    entityId,
    isCreateOrEdit,
    onCreateOrEdit: handleCreateOrEdit,
    currentVariables,
    isCreateOrEditVariable,
    reset,
  }

  return (
    <PageVariablesContext.Provider value={contextValue}>
      <PageContentLayout sx={{ p: 3 }}>
        <Grid container alignItems='center' spacing={0.5} sx={{ mb: 0.5 }}>
          <Grid item xs={2.6}>
            <Typography fontSize={16} fontWeight={600} sx={{ textAlign: 'left' }}>
              {t('configEntity.variables.column.internalId.label')}
            </Typography>
          </Grid>
          <Grid item xs={2.6}>
            <Typography fontSize={16} fontWeight={600} sx={{ textAlign: 'left' }}>
              {t('configEntity.variables.column.variableTitle.label')}
            </Typography>
          </Grid>
          <Grid item xs={2.6}>
            <Typography fontSize={16} fontWeight={600} sx={{ textAlign: 'left' }}>
              {t('configEntity.variables.column.variableType.label')}
            </Typography>
          </Grid>
          <Grid item xs={2.6}>
            <Typography fontSize={16} fontWeight={600} sx={{ textAlign: 'left' }}>
              {t('configEntity.variables.column.valueType.label')}
            </Typography>
          </Grid>
          <Grid item xs={1.4}>
            <Button
              disabled={!isEditEntity || isCreateOrEditVariable || isCreateOrEdit}
              sx={{ minWidth: 'auto' }}
              onClick={handleAddVariable}
            >
              <Add />
              {t('configEntity.variables.button.addVariable')}
            </Button>
          </Grid>
        </Grid>
        <FormProvider
          {...{
            control: methods.control,
            watch: methods.watch,
            handleSubmit: methods.handleSubmit,
            setValue: methods.setValue,
            formState: methods.formState,
            getValues: methods.getValues,
          }}
        >
          {sortedVariables.map((field, index) => (
            <Variable
              key={field.id}
              initialVariables={currentVariables}
              removeVariable={handleRemoveVariable}
              variables={enrichedVariables}
              {...{ index }}
            />
          ))}
        </FormProvider>
      </PageContentLayout>
    </PageVariablesContext.Provider>
  )
}
